import React, { useState, useEffect } from 'react';
import { Brand } from '../../types';
import { useBrandsStore } from '../../store/adminStore';
import Loader from '../loader/Loader';
import Notification from '../toast/Notification';
import Pagination from '../pagination/Pagination';

const BrandManagement: React.FC = () => {
  const { brands, fetchBrands, createBrand, updateBrand, deleteBrand } = useBrandsStore();
  const [loading, setLoading] = useState<boolean>(true);
  const [editingBrand, setEditingBrand] = useState<Brand | null>(null);
  const [newBrandNames, setNewBrandNames] = useState<string>('');
  const [editingName, setEditingName] = useState<string>('');
  const [notification, setNotification] = useState<{ id: number; text: string; status: "success" | "error" | "info" } | null>(null);
  
  // Состояние для поиска и пагинации
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage, setItemsPerPage] = useState<number>(10);

  // Получаем список брендов для предварительного просмотра
  const getPreviewBrands = () => {
    if (!newBrandNames.trim()) return [];
    return newBrandNames
      .split(',')
      .map(name => name.trim())
      .filter(name => name.length > 0);
  };

  // Фильтрация брендов по поисковому запросу
  const filteredBrands = brands.filter(brand =>
    brand.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Пагинация
  const totalPages = Math.ceil(filteredBrands.length / itemsPerPage);
  const paginatedBrands = filteredBrands.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Сброс страницы при изменении поискового запроса
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  useEffect(() => {
    loadBrands();
  }, []);

  const loadBrands = async () => {
    try {
      setLoading(true);
      await fetchBrands();
    } catch (error) {
      console.error('Ошибка при загрузке брендов:', error);
      showNotification('Ошибка при загрузке брендов', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showNotification = (text: string, status: "success" | "error" | "info") => {
    setNotification({ id: Date.now(), text, status });
    setTimeout(() => setNotification(null), 5000);
  };

  const handleCreateBrands = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newBrandNames.trim()) {
      showNotification('Введите названия брендов', 'error');
      return;
    }

    try {
      // Очищаем и разделяем названия по запятой
      const brandNames = newBrandNames
        .split(',')
        .map(name => name.trim())
        .filter(name => name.length > 0);

      if (brandNames.length === 0) {
        showNotification('Введите корректные названия брендов', 'error');
        return;
      }

      await createBrand(brandNames.join(', '));
      setNewBrandNames('');
      
      const count = brandNames.length;
      showNotification(
        count === 1 
          ? 'Бренд успешно добавлен' 
          : `Успешно добавлено ${count} брендов`, 
        'success'
      );
    } catch (error) {
      console.error('Ошибка при создании брендов:', error);
      showNotification('Ошибка при создании брендов', 'error');
    }
  };

  const handleUpdateBrand = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingBrand || !editingName.trim()) {
      showNotification('Введите название бренда', 'error');
      return;
    }

    try {
      await updateBrand(editingBrand.id, editingName.trim());
      setEditingBrand(null);
      setEditingName('');
      showNotification('Бренд успешно обновлен', 'success');
    } catch (error) {
      console.error('Ошибка при обновлении бренда:', error);
      showNotification('Ошибка при обновлении бренда', 'error');
    }
  };

  const handleDeleteBrand = async (id: number) => {
    if (!window.confirm('Вы уверены, что хотите удалить этот бренд?')) {
      return;
    }

    try {
      await deleteBrand(id);
      showNotification('Бренд успешно удален', 'success');
    } catch (error) {
      console.error('Ошибка при удалении бренда:', error);
      showNotification('Ошибка при удалении бренда', 'error');
    }
  };

  const startEditing = (brand: Brand) => {
    setEditingBrand(brand);
    setEditingName(brand.name);
  };

  const cancelEditing = () => {
    setEditingBrand(null);
    setEditingName('');
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return <Loader />;
  }

  return (
    <div className="p-4">
      {notification && (
        <Notification
          id={notification.id}
          text={notification.text}
          status={notification.status}
          duration={5000}
        />
      )}
      
      <h2 className="text-2xl font-bold mb-6">Управление брендами</h2>

      {/* Форма добавления брендов */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <h3 className="text-lg font-semibold mb-4">Добавить бренды</h3>
        <div className="mb-4">
          <p className="text-sm text-gray-600 mb-2">
            Введите названия брендов через запятую для добавления нескольких брендов одновременно:
          </p>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-sm text-blue-800">
              <strong>Пример:</strong> "Nivea, L'Oreal, Garnier, The Ordinary"
            </p>
          </div>
        </div>
        <form onSubmit={handleCreateBrands} className="space-y-4">
          <div className="flex gap-4">
            <input
              type="text"
              value={newBrandNames}
              onChange={(e) => setNewBrandNames(e.target.value)}
              placeholder="Названия брендов через запятую"
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
            <button
              type="submit"
              className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
            >
              Добавить
            </button>
          </div>
          
          {/* Предварительный просмотр */}
          {getPreviewBrands().length > 0 && (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <h4 className="text-sm font-medium text-gray-700 mb-2">
                Будет добавлено брендов: {getPreviewBrands().length}
              </h4>
              <div className="flex flex-wrap gap-2">
                {getPreviewBrands().map((brandName, index) => (
                  <span
                    key={index}
                    className="px-3 py-1 bg-blue-100 text-blue-800 text-sm rounded-full"
                  >
                    {brandName}
                  </span>
                ))}
              </div>
            </div>
          )}
        </form>
      </div>

      {/* Список брендов */}
      <div className="bg-white rounded-lg shadow-md">
        <div className="p-6 border-b border-gray-200">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <h3 className="text-lg font-semibold">
              Список брендов ({filteredBrands.length} из {brands.length})
            </h3>
            
            {/* Поиск */}
            <div className="relative">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Поиск брендов..."
                className="w-full sm:w-64 px-4 py-2 pl-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                >
                  <svg className="h-5 w-5 text-gray-400 hover:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
          </div>
        </div>
        
        {filteredBrands.length === 0 ? (
          <div className="p-6 text-center text-gray-500">
            {searchQuery ? 'Бренды не найдены по запросу' : 'Бренды не найдены'}
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {paginatedBrands.map((brand) => (
              <div key={brand.id} className="p-6 hover:bg-gray-50">
                {editingBrand?.id === brand.id ? (
                  // Режим редактирования
                  <form onSubmit={handleUpdateBrand} className="flex gap-4 items-center">
                    <input
                      type="text"
                      value={editingName}
                      onChange={(e) => setEditingName(e.target.value)}
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                    <button
                      type="submit"
                      className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
                    >
                      Сохранить
                    </button>
                    <button
                      type="button"
                      onClick={cancelEditing}
                      className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
                    >
                      Отмена
                    </button>
                  </form>
                ) : (
                  // Обычный режим отображения
                  <div className="flex justify-between items-center">
                    <div className="flex-1">
                      <h4 className="text-lg font-medium text-gray-900">{brand.name}</h4>
                      <p className="text-sm text-gray-500">
                        Создан: {formatDate(brand.created_at)}
                      </p>
                      {brand.updated_at !== brand.created_at && (
                        <p className="text-sm text-gray-500">
                          Обновлен: {formatDate(brand.updated_at)}
                        </p>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => startEditing(brand)}
                        className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                      >
                        Редактировать
                      </button>
                      <button
                        onClick={() => handleDeleteBrand(brand.id)}
                        className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                      >
                        Удалить
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
        
        {/* Пагинация */}
        {totalPages > 1 && (
          <div className="p-6 border-t border-gray-200">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                <div className="text-sm text-gray-600">
                  Показано {((currentPage - 1) * itemsPerPage) + 1}-{Math.min(currentPage * itemsPerPage, filteredBrands.length)} из {filteredBrands.length} брендов
                </div>
                <div className="flex items-center gap-2">
                  <label htmlFor="itemsPerPage" className="text-sm text-gray-600">
                    На странице:
                  </label>
                  <select
                    id="itemsPerPage"
                    value={itemsPerPage}
                    onChange={(e) => {
                      setItemsPerPage(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                    className="px-3 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value={5}>5</option>
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                  </select>
                </div>
              </div>
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={(page: number) => setCurrentPage(page)}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BrandManagement;
